'use client';

import { type PropsWithChildren, useCallback, useMemo, useRef } from 'react';
import { createContext } from 'use-context-selector';
import type { ProjectCommandResult } from '@frameio/connected-surfaces-core';
import { useSendConnectedSurfaceRequest } from '../hooks/useConnectedSurfaces';
import { useMountedProjectsSync } from '../hooks/useMountedProjectsSync';

export type MountedProjectsContextValue = {
  state: ReturnType<typeof useMountedProjectsSync> & { loading: boolean };
  actions: {
    mountProject: (projectId: string) => Promise<ProjectCommandResult>;
    ejectProject: (projectId: string) => Promise<ProjectCommandResult>;
  };
};

export const MountedProjectsContext =
  createContext<MountedProjectsContextValue | null>(null);

export function MountedProjectsContextProvider({
  children,
}: PropsWithChildren) {
  const {
    projectIds,
    pendingProjectIds,
    ejectingProjectIds,
    status,
    error,
    refresh,
  } = useMountedProjectsSync();
  const request = useSendConnectedSurfaceRequest();
  const available = useRef(false);
  available.current = status !== 'unavailable';

  const mountProject = useCallback(
    (projectId: string) => {
      if (!available.current)
        return Promise.reject(
          new Error('[MountedProjects] messaging is unavailable')
        );
      return request('drive', 'MOUNT_PROJECT', { projectId });
    },
    [request]
  );

  const ejectProject = useCallback(
    (projectId: string) => {
      if (!available.current)
        return Promise.reject(
          new Error('[MountedProjects] messaging is unavailable')
        );
      return request('drive', 'EJECT_PROJECT', { projectId });
    },
    [request]
  );

  const state = useMemo(
    () => ({
      projectIds,
      pendingProjectIds,
      ejectingProjectIds,
      status,
      error,
      refresh,
      loading: status === 'loading',
    }),
    [projectIds, pendingProjectIds, ejectingProjectIds, status, error, refresh]
  );

  const actions = useMemo(
    () => ({ mountProject, ejectProject }),
    [mountProject, ejectProject]
  );
  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <MountedProjectsContext.Provider value={value}>
      {children}
    </MountedProjectsContext.Provider>
  );
}
